import { supabase } from "@/lib/supabaseClient";
import type { Kpi } from "@/services/affiliate";

export type MemberState = "activo" | "crecimiento" | "nuevo" | "inactivo";
export type ActivityLevel = "alta" | "media" | "baja" | "inactiva";

export type TeamMember = {
  id: string;
  fullName: string | null;
  avatarUrl: string | null;
  role: string;
  state: MemberState;
  level: ActivityLevel;
  prospects: number;
  leads: number;
  sales: number;
  activeDays: number;
  /** Actividad de los últimos 5 días (del más antiguo al más reciente). */
  recent: boolean[];
  joinedAt: string;
};

export type LeaderActivity = {
  uid: string;
  fullName: string | null;
  avatarUrl: string | null;
  kind: "joined" | "opener" | "hot" | "sale" | "lesson" | "post";
  at: string;
};

export type LeaderDashboard = {
  weekStart: string;
  weekEnd: string;
  me: { id: string; fullName: string | null; avatarUrl: string | null; role: string; archetype: string | null } | null;
  teamSize: number;
  kpis: { active: Kpi; prospects: Kpi; leads: Kpi; newMembers: Kpi; sales: Kpi };
  members: TeamMember[];
  alerts: { hotNoFollowup: number; inactive: number; newMembers: number };
  series: { day: string; prospects: number; leads: number; sales: number }[];
  seriesTotals: { prospects: { cur: number; prev: number }; leads: { cur: number; prev: number }; sales: { cur: number; prev: number } };
  activity: LeaderActivity[];
  ranking: { id: string; fullName: string | null; avatarUrl: string | null; points: number; archetype: string | null; rank: number; isMe: boolean }[];
};

export async function fetchLeaderDashboard(weekOffset = 0, days = 7): Promise<LeaderDashboard | null> {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const { data, error } = await supabase.rpc("leader_dashboard", { p_tz: tz, p_week_offset: weekOffset, p_days: days });
  if (error) throw error;
  return (data as LeaderDashboard | null) ?? null;
}

export const STATE_LABELS: Record<MemberState, string> = {
  activo: "Activo",
  crecimiento: "En crecimiento",
  nuevo: "Nuevo",
  inactivo: "Inactivo",
};

export function activityText(kind: LeaderActivity["kind"]): { text: string; icon: string; tint: string } {
  switch (kind) {
    case "joined":
      return { text: "se unió a tu equipo", icon: "ri-user-add-line", tint: "bg-violet-50 text-violet-600" };
    case "opener":
      return { text: "envió un mensaje de apertura", icon: "ri-send-plane-line", tint: "bg-sky-50 text-sky-600" };
    case "hot":
      return { text: "tiene un nuevo lead caliente", icon: "ri-fire-line", tint: "bg-orange-50 text-orange-500" };
    case "sale":
      return { text: "logró una venta", icon: "ri-trophy-line", tint: "bg-amber-50 text-amber-500" };
    case "lesson":
      return { text: "completó una lección", icon: "ri-graduation-cap-line", tint: "bg-indigo-50 text-indigo-600" };
    case "post":
      return { text: "publicó en la comunidad", icon: "ri-chat-3-line", tint: "bg-emerald-50 text-emerald-600" };
  }
}

/** Descarga el equipo como CSV (con BOM para que Excel lea bien los acentos). */
export function exportTeamCsv(d: LeaderDashboard): void {
  const esc = (v: string | number) => `"${String(v).replace(/"/g, '""')}"`;
  const rows = [
    ["Afiliado", "Estado", "Prospectos", "Leads", "Ventas", "Días activos (7 d)"],
    ...d.members.map((m) => [m.fullName ?? "Sin nombre", STATE_LABELS[m.state], m.prospects, m.leads, m.sales, m.activeDays]),
  ];
  const csv = "﻿" + rows.map((r) => r.map(esc).join(",")).join("\r\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `equipo-vivelo-${d.weekStart}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
